import * as React from "react";
import ReactHighcharts from "react-highcharts";

import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";

import { TimeSeriesProps, ChartTooltipProps } from "../chart/chart";
import {
  highChartsConfig,
  placeholderData,
  placeholderTickPositions,
} from "../chart/chart.constants";
import { constructDateRangeTooltip, getTickPositions } from "../chart/chart.util";
import { formatOrgDataForTimeSeries } from "../overview.util";

export interface ColumnProps {
  series: TimeSeriesProps;
  accountingPeriodLength: number;
  tooltip?: ChartTooltipProps;
}

export const Column = ({ series, tooltip, accountingPeriodLength }: ColumnProps) => {
  const withoutData = series.data.length === 0;
  const tickPositions = withoutData ? placeholderTickPositions : undefined;
  const data = withoutData ? placeholderData : formatOrgDataForTimeSeries(series.data);

  return (
    <ReactHighcharts
      config={{
        ...highChartsConfig,
        tooltip: {
          useHTML: true,
          formatter: function () {
            // To account for incorrect highcharts typings and make TS happy,
            // see more: https://api.highcharts.com/highcharts/tooltip.formatter
            const self: any = this;
            return constructDateRangeTooltip(
              accountingPeriodLength,
              self.point.x,
              self.point.y,
              self.series.name,
              self.series.color,
              tooltip?.valueSuffix,
            );
          },
        },
        series: [{ ...series, data }],
        chart: {
          ...highChartsConfig.chart,
          type: "column",
        },
        plotOptions: {
          column: {
            pointPadding: 0,
          },
        },
        xAxis: {
          ...highChartsConfig.xAxis,
          tickPositioner: function () {
            let self: any = this;
            return getTickPositions(data, self.tickPositions);
          },
          labels: {
            formatter: function () {
              let self = this;
              if (series.data.length <= 1) {
                return new ZonedMoment(self.value).clone().format("MMM 'YY");
              }
              return self.axis.defaultLabelFormatter.call(self);
            },
          },
        },
        yAxis: {
          ...highChartsConfig.yAxis,
          tickPositions,
        },
      }}
    />
  );
};
